import { AddonApiKey } from '../services/payment.types';
import { usePricing } from './usePricing';
import { UserPlanStatus, useUserPlanStatus } from './useUserPlanStatus';

export interface UserPlanStatusWithTransactionLimit extends UserPlanStatus {
    hasEnoughTransactionLimit: (year: string, transactionCount: number) => boolean;
    getTransactionLimit: (year: string) => number;
    hasWeDoEverythingAddon: (year: string) => boolean;
}

/**
 * Custom hook that extends useUserPlanStatus with transaction limit checking functionality
 * This allows components to use transaction limit checking without breaking existing code
 */
export const useUserPlanStatusWithTransactionLimit = (): UserPlanStatusWithTransactionLimit => {
    // Get the base user plan status hook
    const userPlanStatus = useUserPlanStatus();
    // Get pricing data for transaction limits
    const { pricing } = usePricing();

    /**
     * Check if user has "We Do Everything" addon AND access to a specific tax year
     * @param year The tax year to check
     * @returns True if the user has the addon and access to the tax year, false otherwise
     */
    const hasWeDoEverythingAddon = (year: string): boolean => {
        const hasAddon = userPlanStatus.hasAddon(AddonApiKey.WE_DO_EVERYTHING);
        const hasAccessToYear = userPlanStatus.hasWeDoEverythingAccessToYear(year);
        const result = hasAddon && hasAccessToYear;

        console.log(`hasWeDoEverythingAddon for year ${year}:`, {
            hasAddon,
            hasAccessToYear,
            result,
        });

        return result;
    };

    /**
     * Check if a user has enough transaction limit for a specific tax year
     * @param year The tax year to check
     * @param transactionCount The number of transactions to check against
     * @returns True if the user has enough transaction limit, false otherwise
     */
    const hasEnoughTransactionLimit = (year: string, transactionCount: number): boolean => {
        if (
            userPlanStatus.hasAddon(AddonApiKey.WE_DO_EVERYTHING) &&
            userPlanStatus.hasWeDoEverythingAccessToYear(year)
        ) {
            return true;
        }

        if (!userPlanStatus.isPurchased(year)) return false;

        const planKey = `taxReports.${year}`;
        const transactionLimit = userPlanStatus.userPlans[planKey]?.transactionLimit ?? 0;
        return transactionLimit >= transactionCount;
    };

    /**
     * Get the transaction limit for a specific tax year
     * @param year The tax year to check
     * @returns The transaction limit for the tax year
     */
    const getTransactionLimit = (year: string): number => {
        if (
            userPlanStatus.hasAddon(AddonApiKey.WE_DO_EVERYTHING) &&
            userPlanStatus.hasWeDoEverythingAccessToYear(year)
        ) {
            return 999_999;
        }

        if (!userPlanStatus.isPurchased(year)) return 0;

        const planKey = `taxReports.${year}`;
        return userPlanStatus.userPlans[planKey]?.transactionLimit ?? 0;
    };

    return {
        ...userPlanStatus,
        hasEnoughTransactionLimit,
        getTransactionLimit,
        hasWeDoEverythingAddon,
    };
};
